import React from 'react'
import styled from 'styled-components'
import { Icon } from '../../atoms/Icon'
import type { PropertyTypeIconProps } from './PropertyTypeIcon.types'

// Subtype icons mapping (matches estimate header logic)
const subtypeIcons: Record<string, string> = {
  studio: 'subtype-studio',
  ground_floor_garden: 'subtype-ground-floor-garden',
  ground_floor: 'subtype-ground-floor',
  floor: 'subtype-floor',
  duplex: 'subtype-duplex',
  attic: 'subtype-attic',
  penthouse: 'subtype-attic', // Use attic icon as fallback for penthouse
  detached: 'subtype-detached',
  semi_detached: 'subtype-semi-detached',
  terraced: 'subtype-terraced',
  farmhouse: 'subtype-farmhouse',
}

// Property type configuration
const propertyTypeConfig = {
  house: {
    iconName: 'home' as const,
    color: '#10B981', // Green
    bg: 'rgba(16, 185, 129, 0.1)',
  },
  apartment: {
    iconName: 'building' as const,
    color: '#3B82F6', // Blue
    bg: 'rgba(59, 130, 246, 0.1)',
  },
} as const

// Styled components
const IconContainer = styled.div<{
  $background?: string
  $size?: number
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => $size || 48}px;
  height: ${({ $size }) => $size || 48}px;
  border-radius: ${({ $size }) => {
    const baseSize = $size || 48
    if (baseSize <= 32) return '6px'
    if (baseSize <= 48) return '8px'
    return '10px'
  }};
  background: ${({ $background }) => $background || 'rgba(142, 142, 147, 0.1)'};
`

export const PropertyTypeIcon: React.FC<PropertyTypeIconProps> = ({
  propertyType,
  propertySubtype,
  size = 48,
  className,
  style,
}) => {
  // Calculate icon size based on container size
  const getIconSize = () => {
    if (size <= 32) return 'small'
    if (size <= 48) return 'medium'
    if (size <= 64) return 'large'
    return 'xl'
  }

  // Get subtype icon if available
  const subtypeIcon = propertySubtype ? subtypeIcons[propertySubtype] : null

  // Priority: subtype icon → type config → default
  if (subtypeIcon) {
    // Determine color based on property type (apartments are blue, houses are green)
    const isApartmentSubtype = [
      'studio',
      'ground_floor_garden',
      'ground_floor',
      'floor',
      'duplex',
      'attic',
      'penthouse',
    ].includes(propertySubtype as string)
    const color = isApartmentSubtype ? '#3B82F6' : '#10B981'
    const bg = isApartmentSubtype
      ? 'rgba(59, 130, 246, 0.1)'
      : 'rgba(16, 185, 129, 0.1)'

    return (
      <IconContainer
        $size={size}
        $background={bg}
        className={className}
        style={{ color, ...style }}
      >
        <Icon name={subtypeIcon as any} size={getIconSize()} color={color} />
      </IconContainer>
    )
  }

  const config = propertyType ? propertyTypeConfig[propertyType] : null

  if (!config) {
    // Return default building icon if property type not found
    return (
      <IconContainer
        $size={size}
        $background="rgba(142, 142, 147, 0.1)"
        className={className}
        style={style}
      >
        <Icon name="building" size={getIconSize()} color="#6B6B6B" />
      </IconContainer>
    )
  }

  return (
    <IconContainer
      $background={config.bg}
      $size={size}
      className={className}
      style={{ color: config.color, ...style }}
    >
      <Icon name={config.iconName} size={getIconSize()} color={config.color} />
    </IconContainer>
  )
}

export default PropertyTypeIcon
